// store/tournaments.js
// Namespaced Vuex module — register as `tournaments` in store/index.js (Nuxt v2 auto-registers
// modules under store/ by filename, so this file alone is enough if using the classic store structure).

import { normalizeTournament } from './tournaments.normalize'
import { parseDate } from '~/utils/parseDate'

// Module-level guard (not in Vuex state, since it's not serializable/reactive data) so
// startClock is a safe no-op if dispatched more than once — e.g. from multiple FAB instances.
let clockInterval = null

export const state = () => ({
    list: [], // lightweight list of active + upcoming tournaments — FAB / carousel / promo cards
    detail: {}, // keyed by tournament id — full payload for the promo page
    now: Date.now(), // ticked periodically so `primary` re-evaluates active/upcoming as time passes
    loadingList: false,
    loadingDetail: false
})

export const getters = {
    // Slides for the FAB slider, in display order:
    //   1. tournaments that already started (and haven't ended), the one ending first on top
    //   2. then tournaments that haven't started yet, the one starting first on top
    // Each slide is tagged with `fabState` so the FAB doesn't need to re-derive it:
    //   started + opted in -> 'ranked' (shows the player's position)
    //   started + not opted in -> 'join'
    //   not started yet -> 'upcoming' (shows the start date)
    // Tournaments that already ended are left out.
    fabSlides(state) {
        const now = state.now
        const startMs = (t) => parseDate(t.startsAt).getTime()
        const endMs = (t) => parseDate(t.endsAt).getTime()

        // .filter() returns a new array, so the .sort() calls below never reorder state.list itself
        const started = state.list
            .filter((t) => startMs(t) <= now && endMs(t) > now)
            .sort((a, b) => endMs(a) - endMs(b))
            .map((t) => ({ ...t, fabState: t.optedIn ? 'ranked' : 'join' }))

        const upcoming = state.list
            .filter((t) => startMs(t) > now)
            .sort((a, b) => startMs(a) - startMs(b))
            .map((t) => ({ ...t, fabState: 'upcoming' }))

        return [...started, ...upcoming]
    },
    // First slide, or null when there is nothing to show
    primary(state, getters) {
        return getters.fabSlides[0] || null
    },
    byId: (state) => (id) => state.detail[id] || null
}

export const mutations = {
    SET_LIST(state, tournaments) {
        state.list = tournaments
    },
    SET_DETAIL(state, tournament) {
        // Vue.set not needed here since this is a Vuex module (reactive via the store itself),
        // but if you're not on vuex's built-in reactivity helpers, use Vue.set(state.detail, tournament.id, tournament)
        state.detail = { ...state.detail, [tournament.id]: tournament }
    },
    SET_LEADERBOARD(state, { tournamentId, leaderboard, currentUser }) {
        const existing = state.detail[tournamentId]
        if (!existing) return
        state.detail = {
            ...state.detail,
            [tournamentId]: { ...existing, leaderboard, currentUser }
        }
        // Keep the lightweight `list` entry's position/points in sync too, since the FAB reads from there
        state.list = state.list.map((t) =>
            t.id === tournamentId
                ? { ...t, userPosition: currentUser?.position ?? t.userPosition, userPoints: currentUser?.points ?? t.userPoints }
                : t
        )
    },
    SET_LOADING_LIST(state, loading) {
        state.loadingList = loading
    },
    SET_LOADING_DETAIL(state, loading) {
        state.loadingDetail = loading
    },
    SET_NOW(state, now) {
        state.now = now
    },
    SET_OPTED_IN(state, tournamentId) {
        // tournamentId may arrive as a string (e.g. from a route param) while stored keys/ids are
        // numbers, or vice versa — compare loosely so this doesn't silently no-op on a type mismatch.
        if (state.detail[tournamentId]) {
            state.detail = {
                ...state.detail,
                [tournamentId]: { ...state.detail[tournamentId], optedIn: true }
            }
        }
        state.list = state.list.map((t) => (t.id == tournamentId ? { ...t, optedIn: true } : t)) // eslint-disable-line eqeqeq
    }
}

export const actions = {
    // This only needs to catch state transitions (active -> ended, upcoming -> active), not drive
    // a live countdown display, so a coarse tick is enough — no need for a per-second interval here.
    startClock({ commit }) {
        if (clockInterval) return
        clockInterval = setInterval(() => commit('SET_NOW', Date.now()), 30000)
    },
    stopClock() {
        clearInterval(clockInterval)
        clockInterval = null
    },

    async fetchList({ commit, rootState }) {
        commit('SET_LOADING_LIST', true)
        try {
            // Needs both active AND upcoming tournaments now (case 3), not just active ones —
            // point this at whatever endpoint/params return that combined set.
            const response = await this.$new_cms().get('/api/cms/tournaments')
            const raw = response.data.data
            const currentUser = rootState.auth && rootState.auth.user
            // Each entry arrives as the same nested { data: {...} } detail shape as fetchDetail's
            // response, not a lightweight summary — reuse the same normalizer rather than a second one.
            const tournaments = raw.map((item) => normalizeTournament(item.data || item, currentUser))
            commit('SET_LIST', tournaments)
            // Cache each into `detail` too, so opening the panel for one of these doesn't refetch
            // data we already have in hand.
            tournaments.forEach((t) => commit('SET_DETAIL', t))
        } finally {
            commit('SET_LOADING_LIST', false)
        }
    },

    async fetchDetail({ commit, rootState }, tournamentId) {
        commit('SET_LOADING_DETAIL', true)
        try {
            const raw = await this.$new_cms().get(`/api/cms/tournaments/${tournamentId}`)
            // Adjust rootState.auth.user to wherever the user + opted_in_promotions actually live
            commit('SET_DETAIL', normalizeTournament(raw.data || raw, rootState.auth && rootState.auth.user))
        } finally {
            commit('SET_LOADING_DETAIL', false)
        }
    },

    // Call this right after your own opt-in API call succeeds (e.g. alongside the
    // auth/storeUser commit in TournamentPanel's optIn()). It flips the cached optedIn flag
    // immediately (so the FAB/panel switch UI state without waiting on a network round trip),
    // then kicks off a real fetchDetail in the background so the actual leaderboard — including
    // this user's new row/rank — catches up shortly after, once BE has processed the opt-in.
    markOptedIn({ commit, dispatch }, tournamentId) {
        commit('SET_OPTED_IN', tournamentId)
        dispatch('fetchDetail', tournamentId)
    },

    // Wire this up to whichever Pusher channel/event the mission-alert socket pattern already uses,
    // e.g. bind in a component's mounted() and dispatch this on each event.
    onLeaderboardUpdate({ commit }, payload) {
        // payload: { tournamentId, leaderboard, currentUser }
        commit('SET_LEADERBOARD', payload)
    }
}